import React, { useEffect, useState, useRef, memo } from "react";
import { CrossIcon } from "~/components/icons/cross";
import { useOutsideClick } from "~/hooks/useOutsideClick";
import { body, content, entered, exited, close } from "./index.module.scss";

// Hide FeedbackPopup on all user interaction except on Next, Back and Continue actions
// Also to prevent an animation inconsistency when triggering both QuestionLayout and hide FeedbackPopup transitions at the same time
const ctaExceptions = ["previous-question", "next-question", "continue"];

export const FeedbackPopup = memo(({ onHidePopup, text }) => {
  const element = useRef(null);
  const [animation, setAnimation] = useState(null);

  useOutsideClick(element, (event) => {
    const isSlider = event.target.parentNode.classList.contains("rc-slider");
    const ctaButton = event.target.getAttribute("data-testid");

    // Prevent exited animation when the target is the slider and the CTA exceptions buttons
    if (!isSlider && !ctaExceptions.includes(ctaButton)) {
      setAnimation(exited);
    }
  });

  useEffect(() => {
    if (!animation) {
      deferAnimation(entered);
    }
  }, [animation]);

  // Auto-dismiss the popup after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      if (animation === entered) {
        setAnimation(exited);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [animation]);

  const onClose = () => {
    setAnimation(exited);
  };

  const deferAnimation = (value) => {
    // Defer Animation style by an offset of 20ms to avoid popping or flickers when entering
    setTimeout(() => {
      setAnimation(value);
    }, 20);
  };

  const onTransitionEnd = () => {
    if (animation === exited) {
      setAnimation(null);
      onHidePopup();
    }
  };

  return (
    <div
      ref={element}
      className={`${body} ${animation}`}
      onTransitionEnd={onTransitionEnd}
      data-testid="feedback-popup"
    >
      <div className={content} dangerouslySetInnerHTML={{ __html: text }} />

      <button
        type="button"
        className={close}
        onClick={onClose}
        data-testid="feedback-popup-close"
      >
        <CrossIcon size={13} />
      </button>
    </div>
  );
});

FeedbackPopup.displayName = "FeedbackPopup";
